import { parseDatabaseCellAttachmentSourceId } from '@affine/core/blocksuite/attachment-viewer/database-cell-source-id';
import type { AttachmentViewerSource } from '@affine/core/blocksuite/attachment-viewer/types';
import { resolveBlobDisplayName } from '@affine/core/utils/blob-display-name';

import { getFileCellItems } from '../../../blocksuite/database-block/properties/file/define';

type DatabaseCellPdfSourceOptions = {
  cellValue?: unknown;
  canRename?: boolean;
  rename?: (nextName: string) => void;
  canReplaceBlob?: boolean;
  replaceBlob?: AttachmentViewerSource['replaceBlob'];
};

function isLegacyDatabaseCellAttachmentSourceId(blockId: string): boolean {
  return blockId.startsWith('database-cell:');
}

export function buildDatabaseCellPdfSource(
  blockId: string,
  docId: string,
  options: DatabaseCellPdfSourceOptions = {}
): AttachmentViewerSource | null {
  const parts = parseDatabaseCellAttachmentSourceId(blockId);
  if (!parts) {
    return null;
  }
  const files = getFileCellItems(options.cellValue);
  const file =
    files.find(item => item.refId === parts.refId) ??
    (isLegacyDatabaseCellAttachmentSourceId(blockId)
      ? files.find(item => item.id === parts.blobId)
      : undefined);
  const mimeType = file?.mime ?? 'application/pdf';
  const canRename = options.canRename === true && !!options.rename;
  const canReplaceBlob =
    options.canReplaceBlob === true && !!options.replaceBlob;

  return {
    kind: 'database-cell',
    id: blockId,
    docId,
    blobId: file?.id ?? parts.blobId,
    databaseAttachmentRefId: parts.refId,
    name: resolveBlobDisplayName({
      blobId: parts.blobId,
      occurrenceName: file?.name,
      fallbackName: 'Database cell PDF',
      mimeType,
    }),
    mimeType,
    size: file?.size ?? null,
    canRename,
    rename: canRename ? options.rename : undefined,
    ...(canReplaceBlob
      ? {
          canReplaceBlob: true,
          replaceBlob: options.replaceBlob,
        }
      : {}),
    supportsBlockAnnotations: false,
    supportsPdfSearchIndex: true,
  };
}
